import config from "config";
import {
	TCalibrationResponse,
	TCandidateSort,
	TConfirmablePhase,
	TCreateSearchResponse,
	TCriteriaResponse,
	TCriterion,
	TCriterionCreateRequest,
	TCriterionFromTextResponse,
	TCriterionPatchRequest,
	TCriterionTextRequest,
	THandoffSummaryResponse,
	TPhaseConfirmResponse,
	TPolarisSearchStatus,
	TPostMessageResponse,
	TProjectsResponse,
	TRankedCandidateList,
	TRecentSearchesResponse,
	TResampleResponse,
	TRescueResponse,
	TRoleBasicsResponse,
	TRoleFieldPatchRequest,
	TRuleoutBreakdown,
	TRuleoutDrilldown,
	TSaveToProjectRequest,
	TSaveToProjectResponse,
	TSearchStatusResponse,
	TTranscriptResponse,
	TVoteRequest,
	TVoteResponse,
} from "api/polarisApi/types";
import PolarisSearchResultsApi from "./searchResultsApi";

// Phase 2 · Footprint endpoints live in ./footprintApi and the Phase 5 audit
// and versions endpoints in ./searchResultsApi (inherited, so callers keep
// using polarisApi.getFootprint(), polarisApi.getVersions() and friends).
class PolarisApi extends PolarisSearchResultsApi {
	constructor() {
		// Polaris backend lives on its own domain; auth tokens are shared and
		// token refresh always targets the main auth domain (see api/index.ts).
		super(config.polarisApiUrl as string);
	}

	// Whether the caller's organisation may use Polaris (Internal orgs, plus the
	// client orgs allow-listed on the Polaris service). Never 403s.
	async getAccess(requestConfig = {}) {
		return this.doGet<{enabled: boolean}>(`/polaris/access`, requestConfig);
	}

	async createSearch(brief: string, requestConfig = {}) {
		return this.doPost<TCreateSearchResponse, {brief: string}>(
			`/polaris/searches`,
			{brief},
			requestConfig,
		);
	}

	async getRecentSearches(
		pagination: {limit: number; offset: number; status?: TPolarisSearchStatus} = {
			limit: 10,
			offset: 0,
		},
		requestConfig = {},
	) {
		// status is optional — when set, the backend filters the list (and the
		// returned pagination.total) to that lifecycle status.
		const statusQuery = pagination.status ? `&status=${pagination.status}` : "";

		return this.doGet<TRecentSearchesResponse>(
			`/polaris/searches?limit=${pagination.limit}&offset=${pagination.offset}${statusQuery}`,
			requestConfig,
		);
	}

	// ---- Transcript / chat ----

	async getTranscript(searchId: string, sinceSeq = 0, requestConfig = {}) {
		return this.doGet<TTranscriptResponse>(
			`/polaris/searches/${searchId}/transcript?since_seq=${sinceSeq}`,
			requestConfig,
		);
	}

	async postMessage(searchId: string, text: string, requestConfig = {}) {
		return this.doPost<TPostMessageResponse, {text: string}>(
			`/polaris/searches/${searchId}/messages`,
			{text},
			requestConfig,
		);
	}

	// ---- Phase 1 · Role basics ----

	async getRoleBasics(searchId: string, requestConfig = {}) {
		return this.doGet<TRoleBasicsResponse>(
			`/polaris/searches/${searchId}/role-basics`,
			requestConfig,
		);
	}

	async patchRoleField(
		searchId: string,
		fieldKey: string,
		body: TRoleFieldPatchRequest,
		requestConfig = {},
	) {
		return this.doPatch<TRoleBasicsResponse, TRoleFieldPatchRequest>(
			`/polaris/searches/${searchId}/role-basics/fields/${fieldKey}`,
			body,
			requestConfig,
		);
	}

	// ---- Phase 3 · Calibration ----

	async getCalibration(searchId: string, requestConfig = {}) {
		return this.doGet<TCalibrationResponse>(
			`/polaris/searches/${searchId}/calibration`,
			requestConfig,
		);
	}

	async postVote(searchId: string, body: TVoteRequest, requestConfig = {}) {
		return this.doPost<TVoteResponse, TVoteRequest>(
			`/polaris/searches/${searchId}/calibration/votes`,
			body,
			requestConfig,
		);
	}

	async postResample(searchId: string, requestConfig = {}) {
		return this.doPost<TResampleResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/calibration/resample`,
			{},
			requestConfig,
		);
	}

	// Re-runs the first-sample build; returns the same payload as GET.
	async retryCalibration(searchId: string, requestConfig = {}) {
		return this.doPost<TCalibrationResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/calibration/retry`,
			{},
			requestConfig,
		);
	}

	async getCriteria(searchId: string, requestConfig = {}) {
		return this.doGet<TCriteriaResponse>(
			`/polaris/searches/${searchId}/criteria`,
			requestConfig,
		);
	}

	async retryCriteria(searchId: string) {
		return this.doPost<TCriteriaResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/criteria/retry`,
			{},
		);
	}

	// Free text in, a written criterion out (409 when criteria are not ready,
	// 400 when the text is empty or too long).
	async postCriterionText(searchId: string, text: string, requestConfig = {}) {
		return this.doPost<TCriterionFromTextResponse, TCriterionTextRequest>(
			`/polaris/searches/${searchId}/criteria`,
			{text},
			requestConfig,
		);
	}

	async postCriterion(searchId: string, body: TCriterionCreateRequest, requestConfig = {}) {
		return this.doPost<TCriterion, TCriterionCreateRequest>(
			`/polaris/searches/${searchId}/criteria`,
			body,
			requestConfig,
		);
	}

	async patchCriterion(
		searchId: string,
		criterionId: string,
		body: TCriterionPatchRequest,
		requestConfig = {},
	) {
		return this.doPatch<TCriterion, TCriterionPatchRequest>(
			`/polaris/searches/${searchId}/criteria/${criterionId}`,
			body,
			requestConfig,
		);
	}

	async deleteCriterion(searchId: string, criterionId: string, requestConfig = {}) {
		return this.doDelete<TCriteriaResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/criteria/${criterionId}`,
			undefined,
			requestConfig,
		);
	}

	async confirmPhase(searchId: string, phase: TConfirmablePhase, requestConfig = {}) {
		return this.doPost<TPhaseConfirmResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/phases/${phase}/confirm`,
			{},
			requestConfig,
		);
	}

	// ---- Phase 5 · Search run ----

	async getSearchStatus(searchId: string, requestConfig = {}) {
		return this.doGet<TSearchStatusResponse>(
			`/polaris/searches/${searchId}/search-status`,
			requestConfig,
		);
	}

	async getRuleouts(searchId: string, requestConfig = {}) {
		return this.doGet<TRuleoutBreakdown>(
			`/polaris/searches/${searchId}/ruleouts`,
			requestConfig,
		);
	}

	async getRuleoutCandidates(
		searchId: string,
		ruleoutId: string,
		pagination = {limit: 20, offset: 0},
		requestConfig = {},
	) {
		return this.doGet<TRuleoutDrilldown>(
			`/polaris/searches/${searchId}/ruleouts/${ruleoutId}/candidates?limit=${pagination.limit}&offset=${pagination.offset}`,
			requestConfig,
		);
	}

	async postRescue(
		searchId: string,
		ruleoutId: string,
		candidateRef: string,
		requestConfig = {},
	) {
		return this.doPost<TRescueResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/ruleouts/${ruleoutId}/candidates/${candidateRef}/rescue`,
			{},
			requestConfig,
		);
	}

	// `sort` is sent only when it is not the server's default, so an older
	// backend (which ignores it) gets the same request as before.
	async getCandidates(
		searchId: string,
		pagination = {limit: 15, offset: 0},
		sort: TCandidateSort = "best_fit",
		requestConfig = {},
	) {
		const sortQuery = sort === "match" ? "&sort=match" : "";

		return this.doGet<TRankedCandidateList>(
			`/polaris/searches/${searchId}/candidates?limit=${pagination.limit}&offset=${pagination.offset}${sortQuery}`,
			requestConfig,
		);
	}

	async getHandoffSummary(searchId: string, requestConfig = {}) {
		return this.doGet<THandoffSummaryResponse>(
			`/polaris/searches/${searchId}/handoff-summary`,
			requestConfig,
		);
	}

	async getProjects(requestConfig = {}) {
		return this.doGet<TProjectsResponse>(`/polaris/projects`, requestConfig);
	}

	async saveToProject(searchId: string, body: TSaveToProjectRequest, requestConfig = {}) {
		return this.doPost<TSaveToProjectResponse, TSaveToProjectRequest>(
			`/polaris/searches/${searchId}/save-to-project`,
			body,
			requestConfig,
		);
	}
}

const polarisApi = new PolarisApi();

export default polarisApi;
